import { PropsWithChildren, useEffect, useRef } from 'react';

import { createInstance, Identify } from '@amplitude/analytics-browser';
import type { BrowserOptions } from '@amplitude/analytics-core';
import { z } from 'zod';

import {
  type AmplitudeProductEventPayload,
  type AuthEventProperties,
  buildAuthEventProperties,
  resolveWebBrowserAmplitudeInit,
  shouldSuppressLegacyUserActionWhenAutocaptureEnabled,
  trackLegacyUserAction,
  trackProductEvent,
  type UserActionEnvelope,
  type WebChromeLegacyActionName,
  type WebProductEventName,
} from '@cfa/pathway-fe-shared';

import useAuth from '@/hooks/useAuth';

import { useFeaturePermission } from '@/utils/permissions';

import { OktaUserRaw } from '@/types';

import Constants from 'constants/index';

import { addPlatformPlugin } from './AmplitudePlatformPlugin';
import { addUrlNormalizationPlugin } from './AmplitudeUrlPlugin';
import {
  addUserPropertiesPlugin,
  getWebUserEventProperties,
} from './AmplitudeUserPropertiesPlugin';
import {
  AmplitudeContext,
  type AmplitudeTrackingContextValue,
  type ChromeTrack,
  type ProductTrack,
} from './useAmplitude';
import { useAmplitudeAutocaptureEnabled } from './useAmplitudeAutocaptureEnabled';

const nativeAmplitudeIdsSchema = z.object({
  deviceId: z.string().min(1),
  sessionId: z.number(),
});

interface AmplitudeProviderProps extends PropsWithChildren {
  user?: OktaUserRaw;
  options?: BrowserOptions;
}

export const AmplitudeProvider: React.FC<AmplitudeProviderProps> = ({
  user,
  options,
  children,
}) => {
  const analyticsEnabled = useFeaturePermission('analytics');
  const amplitudeAutocaptureEnabled = useAmplitudeAutocaptureEnabled();

  const amplitudeRef = useRef<ReturnType<typeof createInstance> | null>(null);
  const initAutocaptureEnabledRef = useRef<boolean | null>(null);
  const authDeferredRef = useRef<{
    promise: Promise<AuthEventProperties>;
    resolve: (props: AuthEventProperties) => void;
  } | null>(null);
  const latestAuthRef = useRef<AuthEventProperties | null>(null);

  const { data: auth, isError: authError } = useAuth({ enabled: !!user });

  useEffect(() => {
    const apiKey = Constants.AMPLITUDE_API_KEY;
    if (
      amplitudeRef.current &&
      initAutocaptureEnabledRef.current !== null &&
      initAutocaptureEnabledRef.current !== amplitudeAutocaptureEnabled
    ) {
      void amplitudeRef.current.flush().promise.catch(() => undefined);
      amplitudeRef.current = null;
      initAutocaptureEnabledRef.current = null;
      authDeferredRef.current = null;
    }

    if (analyticsEnabled && apiKey && user && !amplitudeRef.current) {
      let resolve: (props: AuthEventProperties) => void = () => {};
      const promise = new Promise<AuthEventProperties>(r => {
        resolve = r;
      });
      authDeferredRef.current = { promise, resolve };

      const amplitudeInstance = createInstance();
      amplitudeInstance.add(addPlatformPlugin());
      amplitudeInstance.add(addUrlNormalizationPlugin());
      amplitudeInstance.add(
        addUserPropertiesPlugin(user, promise, () => latestAuthRef.current),
      );

      const isNativeWebView = window.native.isNative;
      const parsedNativeAmplitudeIds = isNativeWebView
        ? nativeAmplitudeIdsSchema.safeParse({
            deviceId: window.amplitudeDeviceId,
            sessionId: window.amplitudeSessionId,
          })
        : undefined;

      const trackingInit = resolveWebBrowserAmplitudeInit({
        amplitudeAutocaptureEnabled,
        isNativeWebView,
        nativeIds: parsedNativeAmplitudeIds?.success
          ? parsedNativeAmplitudeIds.data
          : undefined,
      });

      amplitudeInstance.init(apiKey, user['cfa-guid'], {
        ...(options ?? {}),
        ...trackingInit,
      });
      amplitudeRef.current = amplitudeInstance;
      initAutocaptureEnabledRef.current = amplitudeAutocaptureEnabled;
    }

    const instance = amplitudeRef.current;
    const deferred = authDeferredRef.current;
    if (!instance || !user || !deferred) return;

    // Resolve deferred with fallbacks on auth error so events aren't blocked forever
    if (authError && !auth) {
      const fallback: AuthEventProperties = {
        user_country: 'N/A',
        user_audience: 'N/A',
        user_state: 'N/A',
      };
      latestAuthRef.current = fallback;
      deferred.resolve(fallback);
      return;
    }

    if (!auth) return;

    const authProps = buildAuthEventProperties({
      userCountry: auth.country.id,
      userAudience: auth.audience,
      userState: auth.state?.join(','),
    });
    latestAuthRef.current = authProps;
    deferred.resolve(authProps);

    // identify() sets user_properties for Amplitude's user profile/segmentation.
    // The plugin handles event_properties per-event; both are needed.
    const identifyObj = new Identify();
    identifyObj.unset('user_type');
    identifyObj.set('user_locations', user['cfa-locations']);
    identifyObj.set(
      'permission_level',
      getWebUserEventProperties(user).permission_level,
    );
    identifyObj.set('is_webview', String(window.native.isNative));
    identifyObj.set('user_country', auth.country.id);
    identifyObj.set('user_audience', auth.audience);
    identifyObj.set('user_regions', auth.regions);
    identifyObj.set('user_state', auth.state?.join(',') ?? 'N/A');
    identifyObj.set('user_markets', auth.markets);
    identifyObj.set('user_area_support_teams', auth.areaSupportTeams);
    instance.identify(identifyObj);
  }, [
    analyticsEnabled,
    amplitudeAutocaptureEnabled,
    options,
    user,
    auth,
    authError,
  ]);

  const buildEnvelope = (): UserActionEnvelope => ({
    date: new Date().toLocaleDateString(),
    time: new Date().toLocaleTimeString(),
    version: Constants.APP_VERSION,
    env: Constants.ENV,
  });

  const amplitudeTrackSink = (
    eventType: string,
    eventProperties: Record<string, unknown>,
  ) => {
    amplitudeRef.current?.track(eventType, eventProperties);
  };

  const track: ProductTrack = (
    productEvent: WebProductEventName,
    properties: Record<string, unknown> = {},
  ) => {
    const instance = amplitudeRef.current;
    if (!instance) return false;

    const appliedAutocapture =
      initAutocaptureEnabledRef.current ?? amplitudeAutocaptureEnabled;

    trackProductEvent(
      amplitudeTrackSink,
      productEvent,
      buildEnvelope(),
      properties as AmplitudeProductEventPayload<typeof productEvent>,
      appliedAutocapture,
    );
    return true;
  };

  const trackChrome: ChromeTrack = (
    action: WebChromeLegacyActionName,
    properties: Record<string, unknown> = {},
  ) => {
    const instance = amplitudeRef.current;
    if (!instance) return false;
    const appliedAutocapture =
      initAutocaptureEnabledRef.current ?? amplitudeAutocaptureEnabled;

    if (
      shouldSuppressLegacyUserActionWhenAutocaptureEnabled(
        action,
        appliedAutocapture,
      )
    ) {
      return true;
    }

    trackLegacyUserAction(
      amplitudeTrackSink,
      action,
      buildEnvelope(),
      properties,
      appliedAutocapture,
    );
    return true;
  };

  const contextValue: AmplitudeTrackingContextValue = {
    track,
    trackChrome,
  };

  return (
    <AmplitudeContext.Provider value={contextValue}>
      {children}
    </AmplitudeContext.Provider>
  );
};
